import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import assetTransferService, {
  AssetSearchResult,
  AssetTransferDocument,
  AssetTransferRequest,
  TransferRequest,
} from '../../services/assetTransferService';
import { RootState } from '../store';
import { API_URL } from '@/config/api.config';
import axios from 'axios';

// Error response interface
export interface ErrorResponse {
  status?: number;
  code?: string;
  message?: string;
}

// Types
interface AssetTransferState {
  transferRequests: TransferRequest[];
  currentTransferRequest: AssetTransferRequest | null;
  transferDocuments: AssetTransferDocument[];
  searchResults: AssetSearchResult[];
  selectedAssets: AssetSearchResult[];
  loading: boolean;
  error: string | ErrorResponse | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

// Initial state
const initialState: AssetTransferState = {
  transferRequests: [],
  currentTransferRequest: null,
  transferDocuments: [],
  searchResults: [],
  selectedAssets: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 0,
  pageSize: 10,
};

export interface CreateAssetTransferPayload {
  reason: string;
  to_location: string;
  target_custodian: string;
  target_department: string;
  transfer_cost: string | number;
  comments?: string;
  asset_ids: string[];
  vehicle?: string | undefined;
  movement_tracking_enabled: boolean;
  tracking_type?: 'vehicle' | 'asset' | undefined;
  files?: File[];
}

// Async thunks
export const fetchTransferRequests = createAsyncThunk(
  'assetTransfer/fetchTransferRequests',
  async (
    params: {
      page?: number;
      limit?: number;
      status?: string;
      from_date?: string;
      to_date?: string;
      search?: string;
      ordering?: string;
      asset_tags?: string;
      to_location?: string;
      from_location?: string;
      created_by?: string;
      location?: string;
      department?: string;
      asset_name?: string;
      manufacturer?: string;
      model?: string;
      serial_number?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await assetTransferService.getTransferRequests(params);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTransferRequestById = createAsyncThunk(
  'assetTransfer/fetchTransferRequestById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await assetTransferService.getTransferRequestById(id);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createTransferRequest = createAsyncThunk(
  'assetTransfer/createTransferRequest',
  async (data: CreateAssetTransferPayload, { rejectWithValue }) => {
    try {
      
      const requestData = {
        ...data,
        asset_ids: data.asset_ids || [],
      };
      
      // If vehicle is provided, ensure it's included
      if (data.vehicle) {
        Object.defineProperty(requestData, 'vehicle', {
          value: data.vehicle,
          enumerable: true,
          configurable: true,
          writable: true
        });
      }
            
      const response = await assetTransferService.createTransferRequest(requestData);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTransferRequest = createAsyncThunk(
  'assetTransfer/updateTransferRequest',
  async ({ id, data }: { id: string; data: Partial<AssetTransferRequest> }) => {
    const response = await axios.post(
      `${API_URL}/assets/transfers/requests/${id}/submit/`,
      data
    );
    return response.data;
  }
);

export const deleteTransferRequest = createAsyncThunk(
  'assetTransfer/deleteTransferRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      await assetTransferService.deleteTransferRequest(id);
      return id;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const searchAssets = createAsyncThunk(
  'assetTransfer/searchAssets',
  async (
    {
      query,
      categoryId,
      locationId,
      departmentId,
    }: {
      query: string;
      categoryId?: string | string[];
      locationId?: string | string[];
      departmentId?: string | string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await assetTransferService.searchAssets(
        query,
        categoryId,
        locationId,
        departmentId
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadTransferDocument = createAsyncThunk(
  'assetTransfer/uploadTransferDocument',
  async (
    { transferId, file }: { transferId: string; file: File },
    { rejectWithValue }
  ) => {
    try {
      const response = await assetTransferService.uploadTransferDocument(
        transferId,
        file
      );
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTransferDocuments = createAsyncThunk(
  'assetTransfer/fetchTransferDocuments',
  async (transferId: string, { rejectWithValue }) => {
    try {
      const response =
        await assetTransferService.getTransferDocuments(transferId);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createBulkTransferRequest = createAsyncThunk(
  'assetTransfer/createBulkTransferRequest',
  async (
    {
      assetIds,
      transferData,
    }: {
      assetIds: string[];
      transferData: Omit<AssetTransferRequest, 'asset'>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await assetTransferService.createBulkTransferRequest(
        assetIds,
        transferData
      );
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data: ErrorResponse };
        message: string;
      };
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Slice
const assetTransferSlice = createSlice({
  name: 'assetTransfer',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    addSelectedAsset: (state, action: PayloadAction<AssetSearchResult>) => {
      // Check if asset is already selected
      if (
        !state.selectedAssets.some((asset) => asset.id === action.payload.id)
      ) {
        state.selectedAssets.push(action.payload);
      }
    },
    removeSelectedAsset: (state, action: PayloadAction<string>) => {
      state.selectedAssets = state.selectedAssets.filter(
        (asset) => asset.id !== action.payload
      );
    },
    clearSelectedAssets: (state) => {
      state.selectedAssets = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    resetTransferState: (state) => {
      state.currentTransferRequest = null;
      state.transferDocuments = [];
      state.error = null;
    },
    // Action to trigger refetch after create/update operations
    invalidateTransferRequests: (state) => {
      // This action can be dispatched to signal that the list needs to be refetched
      // Components can listen for this action to trigger a refetch
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTransferRequests
      .addCase(fetchTransferRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.transferRequests = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchTransferRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchTransferRequestById
      .addCase(fetchTransferRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransferRequest = action.payload;
      })
      .addCase(fetchTransferRequestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // createTransferRequest
      .addCase(createTransferRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransferRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Don't directly insert the create response into transferRequests
        // as it has a different shape (AssetTransferRequest vs TransferRequest[])
        // The list will be refreshed by the calling component
        state.currentTransferRequest = action.payload;
      })
      .addCase(createTransferRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // updateTransferRequest
      .addCase(updateTransferRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransferRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransferRequest = action.payload;

        // Don't directly update the list with incompatible data shape
        // The list will be refreshed by the calling component if needed
        // Note: updateTransferRequest returns AssetTransferRequest, but transferRequests expects TransferRequest[]
      })
      .addCase(updateTransferRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // deleteTransferRequest
      .addCase(deleteTransferRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransferRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.transferRequests = state.transferRequests.filter(
          (req) => req.id !== action.payload
        );
        if (state.currentTransferRequest?.id === action.payload) {
          state.currentTransferRequest = null;
        }
      })
      .addCase(deleteTransferRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // searchAssets
      .addCase(searchAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // uploadTransferDocument
      .addCase(uploadTransferDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadTransferDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.transferDocuments.push(action.payload);
      })
      .addCase(uploadTransferDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchTransferDocuments
      .addCase(fetchTransferDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferDocuments.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure transferDocuments is always an array
        if (action.payload && Array.isArray(action.payload)) {
          state.transferDocuments = action.payload;
        } else if (action.payload && Array.isArray(action.payload.results)) {
          // Handle case where API returns { results: [...] }
          state.transferDocuments = action.payload.results;
        } else if (
          action.payload &&
          action.payload.documents &&
          Array.isArray(action.payload.documents)
        ) {
          // Handle case where API returns { documents: [...] }
          state.transferDocuments = action.payload.documents;
        } else {
          // Default to empty array if payload is not in expected format
          state.transferDocuments = [];
        }
      })
      .addCase(fetchTransferDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // createBulkTransferRequest
      .addCase(createBulkTransferRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBulkTransferRequest.fulfilled, (state) => {
        state.loading = false;
        // Clear selected assets after successful bulk transfer
        state.selectedAssets = [];
      })
      .addCase(createBulkTransferRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  setCurrentPage,
  setPageSize,
  addSelectedAsset,
  removeSelectedAsset,
  clearSelectedAssets,
  clearSearchResults,
  resetTransferState,
  invalidateTransferRequests,
} = assetTransferSlice.actions;

// Selectors
export const selectTransferRequests = (state: RootState) =>
  state.assetTransfer.transferRequests;
export const selectCurrentTransferRequest = (state: RootState) =>
  state.assetTransfer.currentTransferRequest;
export const selectTransferDocuments = (state: RootState) =>
  state.assetTransfer.transferDocuments;
export const selectSearchResults = (state: RootState) =>
  state.assetTransfer.searchResults;
export const selectSelectedAssets = (state: RootState) =>
  state.assetTransfer.selectedAssets;
export const selectLoading = (state: RootState) => state.assetTransfer.loading;
export const selectError = (state: RootState) => state.assetTransfer.error;
export const selectPagination = (state: RootState) => ({
  totalCount: state.assetTransfer.totalCount,
  currentPage: state.assetTransfer.currentPage,
  pageSize: state.assetTransfer.pageSize,
});

export default assetTransferSlice.reducer;
